// DE readiness (DE-WIZARD, review #12): where a DE is on its way to work, the server's one word
// (`DigitalEmployeeSummary.readiness`) — Draft → Awaiting approval → Deploying → Live, or Not
// live with why. The roster's chip, the profile header's badge and the profile's readiness
// strip (the four steps with the current one marked, the reason under it) all read it here.
import { Badge, cn } from "@companyos/ui";
import type { DeSummary } from "../../api/types";

export type Readiness = DeSummary["readiness"];
export type ReadinessState = Readiness["state"];

export const READINESS_LABELS: Record<ReadinessState, string> = {
  draft: "Draft",
  awaiting_approval: "Awaiting approval",
  deploying: "Deploying",
  live: "Live",
  not_live: "Not live",
};

const TONE = {
  draft: "neutral",
  awaiting_approval: "warning",
  deploying: "info",
  live: "success",
  not_live: "danger",
} as const satisfies Record<ReadinessState, string>;

/** The reason without its closing full stop (it follows a label in a chip or a tooltip). */
export const readinessReason = (r: Readiness) => r.reason?.replace(/\.$/, "") ?? null;

export function ReadinessBadge({
  readiness,
  className,
  unobserved = false,
}: {
  readiness: Readiness;
  className?: string;
  /**
   * WF-FIX: the caller has no activity to back "Live" (a roster row): a live DE reads "Active"
   * there, never "Live" (`liveness.tsx` earns Live from a recent run or MCP call).
   */
  unobserved?: boolean;
}) {
  if (unobserved && readiness.state === "live")
    return (
      <Badge variant="success" dot data-readiness="active" className={className}>
        Active
      </Badge>
    );
  const reason = readinessReason(readiness);
  return (
    <Badge
      variant={TONE[readiness.state]}
      dot
      data-readiness={readiness.state}
      title={reason ?? undefined}
      className={className}
    >
      {READINESS_LABELS[readiness.state]}
      {reason ? <span className="sr-only">: {reason}</span> : null}
    </Badge>
  );
}

const PATH: ReadinessState[] = ["draft", "awaiting_approval", "deploying", "live"];

/** The self-serve path has no approval step: Draft → Deploying → Live. */
const SELF_SERVE_PATH: ReadinessState[] = ["draft", "deploying", "live"];

/**
 * The steps to Live, the current one marked (a Not live DE marks the step it stopped at: Live
 * when it was live, else Deploying), with the reason under them. Four on the done-for-you path
 * (or when the path is unknown); the self-serve path (`path="self_serve"`) skips "Awaiting
 * approval", which it never passes through.
 */
export function ReadinessStrip({
  readiness,
  done,
  path,
  liveLabel,
}: {
  readiness: Readiness;
  done?: boolean;
  path?: "self_serve" | "done_for_you" | undefined;
  /** WF-FIX: the last step's label when the DE is live ("Idle" without recent activity). */
  liveLabel?: string | undefined;
}) {
  const steps = path === "self_serve" ? SELF_SERVE_PATH : PATH;
  const at =
    readiness.state === "not_live"
      ? steps.indexOf(/paused|retired/i.test(readiness.reason ?? "") ? "live" : "deploying")
      : Math.max(0, steps.indexOf(readiness.state));
  return (
    <div className="flex min-w-0 flex-col gap-2" data-slot="readiness-strip">
      <ol className="flex flex-wrap items-center gap-1.5 text-xs" aria-label="Readiness">
        {steps.map((s, i) => {
          const current = i === at;
          const failed = current && readiness.state === "not_live";
          return (
            <li
              key={s}
              aria-current={current ? "step" : undefined}
              data-state={i < at || (done && current) ? "done" : current ? "current" : "todo"}
              className="flex items-center gap-1.5"
            >
              {i > 0 ? (
                <span aria-hidden="true" className="text-faint">
                  →
                </span>
              ) : null}
              <span
                className={cn(
                  "rounded-sm px-1.5 py-0.5",
                  current
                    ? failed
                      ? "bg-danger-soft font-medium text-danger-text"
                      : "bg-primary-soft font-medium text-primary-text"
                    : i < at
                      ? "text-secondary"
                      : "text-faint",
                )}
              >
                {failed
                  ? READINESS_LABELS.not_live
                  : s === "live" && current && liveLabel
                    ? liveLabel
                    : READINESS_LABELS[s]}
              </span>
            </li>
          );
        })}
      </ol>
      {readiness.reason ? (
        <p className="text-xs text-muted-foreground" data-slot="readiness-reason">
          {readiness.reason}
        </p>
      ) : null}
    </div>
  );
}
